import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Info, RefreshCw, XCircle } from 'lucide-react';

/**
 * Κάρτα καταστήματος → «Στήσιμο» (25/10/2026): τι λείπει για να πουλήσει ένα κατάστημα. Τα γεγονότα τα δίνει ο server
 * (GET /api/support/stores/:id/setup)· εδώ μόνο η ετικέτα, τι σημαίνει και ΠΟΥ διορθώνεται. Ό,τι το support δεν φτάνει
 * ακόμη (εκτυπωτές, τερματικά, πλατφόρμες) το λέει με το όνομα της οθόνης του καταστήματος.
 */

export type SetupStatus = 'ok' | 'missing' | 'warn' | 'info' | 'pending';
export interface SetupItem {
  key: string;
  status: SetupStatus;
  [fact: string]: any;
}

const PLATFORM = (p: string) => ({ efood: 'efood', wolt: 'Wolt', box: 'BOX' } as Record<string, string>)[p] || p;
const DETAIL = (k: string) => ({ vat: 'ΑΦΜ', doy: 'ΔΟΥ', address: 'διεύθυνση' } as Record<string, string>)[k] || k;

/** [τίτλος, τι ισχύει τώρα] ανά στοιχείο της λίστας. */
const describe = (i: SetupItem): [string, string] => {
  switch (i.key) {
    case 'details':
      return ['Στοιχεία καταστήματος', i.status === 'ok' ? 'ΑΦΜ, ΔΟΥ και διεύθυνση συμπληρωμένα.'
        : `Λείπουν: ${(i.missing || []).map(DETAIL).join(', ')} — τα συμπληρώνει ο super admin (Καταστήματα → επεξεργασία).`];
    case 'manager':
      return ['Διαχειριστής', i.status === 'ok' ? `${i.count} ενεργός διαχειριστής.`
        : 'Κανένας ενεργός διαχειριστής — κανείς δεν μπαίνει στις ρυθμίσεις του καταστήματος.'];
    case 'chain':
      return ['Αλυσίδα', i.organization ? `${i.organization}${i.price_list ? ` · τιμοκατάλογος «${i.price_list}»` : ''}` : 'Ανεξάρτητο κατάστημα.'];
    case 'catalog':
      return ['Κατάλογος', i.status === 'ok' ? `${i.products} προϊόντα.`
        : i.pending > 0 ? `Κανένα προϊόν ακόμη· ${i.pending} αλλαγές της αλυσίδας περιμένουν (μπαίνουν στο πρώτο Ζ).`
          : 'Κανένα προϊόν ακόμη.'];
    case 'fiscal':
      if (i.status === 'warn') return ['Φορολογικά', 'Χωρίς πάροχο: το κατάστημα δεν εκδίδει αποδείξεις.'];
      return ['Φορολογικά (Oxygen)', i.status === 'ok' ? `Token και ${i.sequences} σειρές αρίθμησης.`
        : !i.token ? 'Λείπει το token του Oxygen — το βάζει ο super admin.' : 'Λείπουν οι σειρές αρίθμησης.'];
    case 'agent':
      return ['Print agent', i.status === 'ok' ? `Συνδεδεμένος σε ${i.connected_pcs} ${i.connected_pcs === 1 ? 'υπολογιστή' : 'υπολογιστές'}.`
        : i.has_key ? 'Το κλειδί υπάρχει, αλλά κανένας υπολογιστής δεν είναι συνδεδεμένος τώρα.'
          : 'Δεν έχει βγει κλειδί agent — Ρυθμίσεις → Εκτυπωτές του καταστήματος.'];
    case 'printers':
      return ['Εκτυπωτής αποδείξεων', i.status === 'ok' ? `${i.receipt_printers} για αποδείξεις, ${i.printers} συνολικά.`
        : i.printers > 0 ? 'Κανένας εκτυπωτής δεν βγάζει αποδείξεις — Ρυθμίσεις → Εκτυπωτές → «Τι πάει πού».'
          : 'Κανένας εκτυπωτής — Ρυθμίσεις → Εκτυπωτές του καταστήματος.'];
    case 'pos':
      return ['Τερματικά καρτών', i.status === 'ok' ? `${i.terminals} ενεργά.` : 'Κανένα τερματικό: μόνο μετρητά ή κάρτες σε ξεχωριστό POS.'];
    case 'platforms': {
      const enabled: string[] = i.enabled || [];
      const missing = enabled.filter(p => !(i.credentials || []).includes(p));
      const hidden: string[] = i.hidden || [];
      const parts = [
        missing.length ? `Χωρίς σωστή σύνδεση: ${missing.map(PLATFORM).join(', ')} — δεν έρχονται παραγγελίες.` : '',
        hidden.length ? `Παίρνει παραγγελίες ενώ φαίνεται κλειστή: ${hidden.map(PLATFORM).join(', ')}.` : '',
      ].filter(Boolean);
      if (parts.length) return ['Πλατφόρμες', `${parts.join(' ')} Καρτέλα «Πλατφόρμες».`];
      if (!enabled.length) return ['Πλατφόρμες', 'Καμία πλατφόρμα (efood / Wolt / BOX).'];
      return ['Πλατφόρμες', `Ενεργές: ${enabled.map(PLATFORM).join(', ')}.`];
    }
    case 'scheduler':
      return ['Αυτόματες εργασίες', i.status === 'ok' ? 'Ο server ξαναστέλνει μόνος του όσα παραστατικά αποτύχουν.'
        : 'Ο server ΔΕΝ τρέχει τις αυτόματες εργασίες για αυτή την αλυσίδα — πες το στον super admin.'];
    case 'first_receipt':
      return ['Πρώτη απόδειξη', i.status === 'ok' ? 'Έχει βγει απόδειξη με MARK από την ΑΑΔΕ.' : 'Δεν έχει βγει ακόμη απόδειξη με MARK.'];
    case 'anydesk':
      return ['AnyDesk', i.status === 'ok' ? 'Δηλωμένο.' : 'Δεν έχει δηλωθεί — ο super admin το βάζει στο κατάστημα.'];
    case 'billing':
      return ['Χρέωση', i.status === 'ok' ? 'Εγκατάσταση, κύκλος και τιμή γραμμένα.'
        : i.installed_at ? 'Λείπει η συμφωνημένη τιμή — καρτέλα «Χρεώσεις».'
          : 'Δεν έχει γραφτεί εγκατάσταση και τιμή — καρτέλα «Χρεώσεις».'];
    default:
      return [i.key, i.status];
  }
};

const ICON: Record<SetupStatus, { Icon: React.ElementType; cls: string; label: string }> = {
  ok: { Icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400', label: 'Έτοιμο' },
  missing: { Icon: XCircle, cls: 'text-red-600 dark:text-red-400', label: 'Λείπει' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400', label: 'Προσοχή' },
  pending: { Icon: Clock, cls: 'text-sky-600 dark:text-sky-400', label: 'Αναμονή' },
  info: { Icon: Info, cls: 'text-t3', label: 'Πληροφορία' },
};

/** Πόσα «λείπει» — ο αριθμός στην καρτέλα της κάρτας. */
export const setupMissing = (items: SetupItem[] | null): number => (items || []).filter(i => i.status === 'missing').length;

const SupportSetup: React.FC<{ items: SetupItem[] | null; onRefresh: () => void; refreshing?: boolean; justCreated?: string | null }> = ({
  items, onRefresh, refreshing = false, justCreated = null,
}) => {
  if (!items) return <p className="text-sm text-t3">Η λίστα ελέγχου δεν φορτώθηκε.</p>;
  const counted = items.filter(i => i.status !== 'info');
  const ready = counted.filter(i => i.status === 'ok').length;
  return (
    <div className="space-y-3" data-testid="support-setup">
      {justCreated && (
        <p className="text-sm rounded-lg px-3 py-2 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300" data-testid="support-setup-created">{justCreated}</p>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-t2"><b className="text-t1">{ready} από {counted.length}</b> έτοιμα</p>
        <button type="button" onClick={onRefresh} disabled={refreshing}
          className="inline-flex items-center gap-1.5 text-xs text-t3 hover:text-t1 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />Ξανά έλεγχος
        </button>
      </div>
      <ul className="divide-y divide-line border border-line-strong rounded-xl overflow-hidden">
        {items.map(i => {
          const [title, text] = describe(i);
          const { Icon, cls, label } = ICON[i.status] || ICON.info;
          return (
            <li key={i.key} className="flex items-start gap-3 px-3 py-2.5 bg-surface-2 dark:bg-surface-2/40" data-testid={`support-setup-${i.key}`} data-status={i.status}>
              <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${cls}`} aria-label={label} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-t1">{title}</p>
                <p className="text-xs text-t3">{text}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SupportSetup;
